import { useMemo } from 'react';
import { useCustomPreferences } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { FEATURE_PREFERENCE_MAP, RECAPTCHA_CUSTOM_PREFERENCES } from '../recaptcha';
import type {
    RecaptchaEnterpriseConfig,
    RecaptchaFeature,
    RecaptchaFeatureConfig,
} from '../recaptcha';

export interface UseRecaptchaFeatureResult {
    config: RecaptchaFeatureConfig | null;
    enterpriseConfig: RecaptchaEnterpriseConfig | null;
    isEnabled: boolean;
    error: string | null;
}

export const useRecaptchaFeature = (feature: RecaptchaFeature): UseRecaptchaFeatureResult => {
    const preferences = useCustomPreferences(RECAPTCHA_CUSTOM_PREFERENCES);

    const result = useMemo(() => {
        const projectId = preferences[CustomPreference.RECAPTCHA_PROJECT_ID];
        const apiKey = preferences[CustomPreference.RECAPTCHA_API_KEY];
        const siteKeyV3 = preferences[CustomPreference.RECAPTCHA_V3_SITE_KEY];
        const siteKeyV2 = preferences[CustomPreference.RECAPTCHA_V2_SITE_KEY];

        const enterpriseConfig: RecaptchaEnterpriseConfig | null =
            projectId && apiKey && siteKeyV3 && siteKeyV2
                ? {
                      projectId: typeof projectId === 'string' ? projectId : String(projectId),
                      apiKey: typeof apiKey === 'string' ? apiKey : String(apiKey),
                      siteKeyV3: typeof siteKeyV3 === 'string' ? siteKeyV3 : String(siteKeyV3),
                      siteKeyV2: typeof siteKeyV2 === 'string' ? siteKeyV2 : String(siteKeyV2),
                  }
                : null;

        if (!enterpriseConfig) {
            return {
                config: null,
                enterpriseConfig: null,
                isEnabled: false,
                error: 'reCAPTCHA Enterprise configuration is incomplete',
            };
        }

        const featureMap = FEATURE_PREFERENCE_MAP[feature];
        if (!featureMap) {
            return {
                config: null,
                enterpriseConfig,
                isEnabled: false,
                error: `Unknown reCAPTCHA feature: ${feature}`,
            };
        }

        const enabledValue = preferences[featureMap.enabledPref];
        const enableV2FallbackValue = preferences[featureMap.v2FallbackPref];

        const enabled = enabledValue === 'true' || enabledValue === true;
        const enableV2Fallback = enableV2FallbackValue === 'true' || enableV2FallbackValue === true;

        const config: RecaptchaFeatureConfig = {
            enabled,
            enableV2Fallback,
        };

        return {
            config,
            enterpriseConfig,
            isEnabled: enabled,
            error: null,
        };
    }, [feature, preferences]);

    return result;
};
